import { JsonRpcProvider, keccak256, Wallet, HDNodeWallet } from 'ethers'
import {
  EVMGasType,
  NETWORK_CONFIG,
  TXIDVersion,
  getEVMGasTypeForTransaction,
  type NetworkName,
  type TransactionGasDetails
} from '@railgun-community/shared-models'
import { loadRailgunSdk } from '@web/utils/railgunSdk'

import { fetchNativeAssetUsdPrice } from '@web/utils/railgunTokens'
import { FEATURES } from '@web/constants/features'
import {
  sdkInvokeInOffscreen,
  clearMerkletreeRamCacheInOffscreen,
  isRailgunOffscreenHostAvailable
} from '@web/extension-services/background/offscreen/railgunOffscreenClient'
import {
  suspendScanWorkerForChain,
  resumeScanWorkerForChain
} from '@web/extension-services/offscreen/scanWorker/railgunScanWorkerClient'

import { RAILGUN_DEBUG, RAILGUN_SHARED_SCAN_DB, getNetworkNameForChainId } from './railgunCore'

/**
 * P3 (railgun-spend-engine-warm-plan.md §5 step 5). Before a private
 * estimate/proof runs on the main offscreen engine, drop its stale in-RAM state
 * for this chain so it re-reads what the per-chain view-only worker (+ relabel)
 * wrote into the shared DB:
 *   - the UTXO merkletree cache (root + tree lengths), so it sees appended leaves;
 *   - the canonical wallet's receive/sent commitment cache (when `walletId` given),
 *     so `getTokenBalances` re-reads the relabeled notes + completed POI instead of
 *     the pre-copy view (the bug where balance showed total but spendable was 0 /
 *     "pending POI" until a popup reload).
 * No-op unless `RAILGUN_SHARED_SCAN_DB` is on; best-effort so a failure never blocks
 * the spend.
 *
 * Two hosts, one job. On the extension the engine lives in the MV3 offscreen
 * document, so the cache is dropped over the offscreen RPC client. On the
 * mobile-as-webapp / native target there is no offscreen host — the engine runs
 * in THIS process (the nested railgun worker) — so the cache is dropped directly
 * on the in-process engine (see {@link clearMerkletreeRamCacheInProcess}).
 *
 * The mobile-web path used to be skipped entirely on the assumption the relabel
 * path already re-syncs the engine, but `relabelChainAfterWorkerScan` only calls
 * `invalidateCommitmentsCache` (the wallet's receive/sent cache) — it never drops
 * the UTXO merkletree's RAM cache. So the canonical engine's merkletree cache
 * froze at its first read while the per-chain scan worker kept appending leaves to
 * the shared DB behind its back; the next proof was built against a stale root and
 * the JoinSplit circuit's MerkleProofVerifier asserted ("SnarkJS failed to
 * fullProveRailgun" / ERROR: 4), so unshield/transfer failed even though gas
 * estimation (a dummy proof that skips the circuit) had passed.
 */
export async function warmMainEngineForChain(chainId: number, walletId?: string): Promise<void> {
  if (!RAILGUN_SHARED_SCAN_DB) return
  if (isRailgunOffscreenHostAvailable) {
    try {
      await clearMerkletreeRamCacheInOffscreen(chainId, walletId)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Railgun] warmMainEngineForChain failed (shared-DB POC)', chainId, err)
    }
    return
  }
  await clearMerkletreeRamCacheInProcess(chainId, walletId)
}

/**
 * Hold the UTXO merkletree STILL for the duration of a real proof (Trello #322:
 * "swap again while the previous private tx is still Pending" → MerkleProofVerifier
 * ERROR: 4).
 *
 * `Merkletree.getMerkleProof` reads the leaf, then the 16 sibling hashes, then the
 * root — as a sequence of independent, UNLOCKED reads. Anything that inserts leaves
 * between those reads yields a TORN proof: siblings from one tree state, root from
 * another. The circuit then asserts and snarkJS fails, while gas estimation (a dummy
 * proof that skips the circuit) had happily passed.
 *
 * Two writers can tear that read, and both are live exactly when the user spends
 * again right after a spend — that is the whole repro:
 *  1. the engine's own `commitmentListener`, installed by `loadProvider`, which fires
 *     on the provider's block polling the moment the previous tx is mined and inserts
 *     its commitments (`shouldUpdateTrees = true`). Its only guard is
 *     `if (!utxoMerkletree.isScanning)` — a flag proving never sets.
 *  2. under D2, the per-chain scan worker, writing the SAME shared IndexedDB from
 *     another realm, where that flag isn't even visible.
 *
 * So quiesce both: raise the SDK's own `isScanning` flag (exactly how the SDK itself
 * blocks mutation — see `fullRescanUTXOMerkletreesAndWallets`) and park the chain's
 * scan worker. Then drop the RAM cache, so the now-frozen tree is re-read fresh and
 * consistently. Returns a release fn that is ALWAYS safe to call and must run in a
 * `finally` — leaving `isScanning` raised would freeze balance scans for that chain.
 *
 * Best-effort like {@link warmMainEngineForChain}: never throws, so it can't turn a
 * provable spend into a failed one. Extension keeps its existing behaviour (the
 * engine lives in the offscreen document, which clears its own cache before every
 * spend); this in-process quiescence is for mobile-web / native.
 */
export async function beginMerkletreeQuiescenceForChain(
  chainId: number,
  walletId?: string
): Promise<() => void> {
  if (isRailgunOffscreenHostAvailable) {
    await warmMainEngineForChain(chainId, walletId)
    return () => {}
  }

  const networkName = getNetworkNameForChainId(chainId)
  const chain = networkName ? NETWORK_CONFIG[networkName]?.chain : undefined

  let merkletree: { isScanning?: boolean } | undefined
  try {
    const sdk = await loadRailgunSdk()
    const engine = (sdk as { getEngine?: () => unknown }).getEngine?.()
    if (chain && engine && typeof (engine as any).getUTXOMerkletree === 'function') {
      merkletree = (engine as any).getUTXOMerkletree(TXIDVersion.V2_PoseidonMerkle, chain)
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[Railgun] merkletree quiescence: engine unavailable', chainId, err)
  }

  // If a scan is genuinely running we must NOT clear the flag underneath it on
  // release, so remember whether we were the one who raised it.
  const raisedByUs = !!merkletree && merkletree.isScanning !== true
  if (raisedByUs && merkletree) merkletree.isScanning = true
  suspendScanWorkerForChain(chainId)

  // Drop the stale RAM cache only AFTER the tree is frozen, so what we re-read
  // stays valid for the whole proof. Guarded: a throw here must not escape with
  // `isScanning` still raised — that would freeze this chain's scans for good.
  try {
    await warmMainEngineForChain(chainId, walletId)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[Railgun] merkletree quiescence: cache clear failed', chainId, err)
  }

  let released = false
  return () => {
    if (released) return
    released = true
    if (raisedByUs && merkletree) merkletree.isScanning = false
    resumeScanWorkerForChain(chainId)
  }
}

/**
 * Mobile-web / native equivalent of the offscreen registry's
 * `__clearMerkletreeRamCacheForChain`: reset the in-process engine's UTXO
 * merkletree RAM cache (`cachedNodeHashes` incl. the cached root + `treeLengths`)
 * for one chain, plus the canonical wallet's commitment cache, so the next
 * estimate/proof re-reads the leaves the per-chain scan worker appended into the
 * shared DB. RAM-only — the DB is untouched, so it's harmless when the engine
 * scanned the chain itself (it just re-reads identical bytes). Best-effort: any
 * failure is logged, never thrown, so it can't block an already-slow spend.
 */
async function clearMerkletreeRamCacheInProcess(chainId: number, walletId?: string): Promise<void> {
  const networkName = getNetworkNameForChainId(chainId)
  const chain = networkName ? NETWORK_CONFIG[networkName]?.chain : undefined
  if (!chain) return
  try {
    const sdk = await loadRailgunSdk()
    const engine = (sdk as { getEngine?: () => unknown }).getEngine?.()
    const merkletree =
      engine && typeof (engine as { getUTXOMerkletree?: unknown }).getUTXOMerkletree === 'function'
        ? (
            engine as {
              getUTXOMerkletree: (v: TXIDVersion, c: unknown) => Record<string, unknown> | undefined
            }
          ).getUTXOMerkletree(TXIDVersion.V2_PoseidonMerkle, chain)
        : undefined
    if (!merkletree) return
    // We poke private merkletree fields (same as the offscreen registry). If a
    // future SDK renames/removes them, fail loud instead of silently no-op'ing — a
    // silent miss leaves the stale tree in place and the proof keeps failing.
    if (!('cachedNodeHashes' in merkletree) || !('treeLengths' in merkletree)) {
      // eslint-disable-next-line no-console
      console.error(
        '[Railgun] in-process cache-clear: merkletree missing cachedNodeHashes/treeLengths',
        '— SDK internals changed; D2 warm path is broken until updated'
      )
      return
    }
    merkletree.cachedNodeHashes = {}
    merkletree.treeLengths = []
    if (walletId) {
      const wallet = (
        sdk as { walletForID?: (id: string) => { invalidateCommitmentsCache?: (c: unknown) => void } }
      ).walletForID?.(walletId)
      wallet?.invalidateCommitmentsCache?.(chain)
    }
    // TEMP-DIAG(#352): confirm the mobile-web in-process cache-clear actually fires.
    // eslint-disable-next-line no-console
    console.warn('[Railgun] DIAG in-process merkletree RAM cache cleared', { chainId, walletId })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[Railgun] in-process merkletree cache-clear failed', chainId, err)
  }
}

/**
 * A JoinSplit / Unshield ZK proof that fails during witness generation — the
 * circom `MerkleProofVerifier` assert, surfaced by the SDK as
 * "SnarkJS failed to fullProveRailgun" / "Native-prover failed to fullProveRailgun"
 * / "Proof verification failed" — means the local UTXO merkletree is inconsistent
 * with the note being spent: the note's commitment no longer hashes up to the tree
 * root. Gas estimation can't detect this (it uses a DUMMY proof that skips the
 * circuit), so the failure only appears at real proving time. Retrying is futile
 * until the tree is rebuilt from chain data, so the generic "try again or use
 * another token" message is actively misleading. Detect the signature — walking the
 * `cause` chain, since the SDK wraps the raw circom error — so callers can trigger a
 * full rescan and show an accurate message.
 */
export function isProvingMerkleFailure(err: unknown): boolean {
  const parts: string[] = []
  let cur: unknown = err
  for (let depth = 0; cur && depth < 6; depth += 1) {
    parts.push(cur instanceof Error ? cur.message : String(cur))
    cur = cur instanceof Error ? (cur as { cause?: unknown }).cause : undefined
  }
  const hay = parts.join(' ').toLowerCase()
  return (
    hay.includes('failed to fullprove') ||
    hay.includes('proof verification failed') ||
    hay.includes('merkleproofverifier') ||
    hay.includes('invalid merkle') ||
    (hay.includes('assert failed') && hay.includes('merkle'))
  )
}

/**
 * A gas-estimate/transact revert of `RailgunLogic: Note already spent` means the
 * input note the SDK picked has already been nullified on-chain, but the wallet's
 * local spent-state is behind: it still lists that note as Spendable and offers it
 * to the SDK. This happens right after a spend (the nullifier is in a fresh block
 * the wallet hasn't scanned yet) and — more persistently — when one account has two
 * RAILGUN wallets sharing the same notes (passphrase-collision), where one wallet
 * spends a note the other hasn't scanned as spent. A light incremental balance
 * refresh (scanning the new blocks) picks up the nullifier and drops the note, after
 * which the retry no longer selects it. Detect the signature — walking the `cause`
 * chain since the revert is wrapped — so callers can trigger that refresh and show
 * an accurate "syncing, retry" message instead of the generic "token unsupported" one.
 */
export function isNoteAlreadySpentError(err: unknown): boolean {
  const parts: string[] = []
  let cur: unknown = err
  for (let depth = 0; cur && depth < 6; depth += 1) {
    parts.push(cur instanceof Error ? cur.message : String(cur))
    cur = cur instanceof Error ? (cur as { cause?: unknown }).cause : undefined
  }
  return parts.join(' ').toLowerCase().includes('note already spent')
}

// One rescan per chain at a time — a full UTXO rescan is expensive (minutes on
// Ethereum's ~230k-leaf tree) and repeated failed proof attempts must not stack
// duplicate rescans on top of each other.
const inFlightUTXORescans = new Set<number>()

/**
 * Rebuild the UTXO merkletree (and re-derive wallet balances) for one chain from
 * chain data via the SDK's `rescanFullUTXOMerkletreesAndWallets`. This is the
 * canonical remedy for a {@link isProvingMerkleFailure}: it re-reads every leaf and
 * recomputes all node hashes, so any local tree inconsistency that broke the merkle
 * proof is resolved and the user's next spend attempt can prove.
 *
 * Best-effort and fire-and-forget: it runs in the background so it never extends
 * the (already-failed) proof operation past its timeout, and any failure is logged
 * rather than thrown. It runs the SDK call directly, so it only works where the
 * engine lives in THIS process — the mobile-web nested railgun worker and native.
 * The extension runs the engine in an offscreen document (proving is delegated
 * there), so a direct SDK call here would miss it; that path is skipped (the
 * reported bug is mobile-web only).
 */
export async function triggerFullUTXORescanForChain(
  chainId: number,
  walletId?: string
): Promise<void> {
  if (FEATURES.railgunEngineOffscreenEnabled) return
  if (inFlightUTXORescans.has(chainId)) return

  const networkName = getNetworkNameForChainId(chainId)
  const chain = networkName ? NETWORK_CONFIG[networkName]?.chain : undefined
  if (!chain) return

  inFlightUTXORescans.add(chainId)
  // The rescan CLEARS this chain's namespace before rebuilding it. Under D2 the
  // per-chain scan worker writes into that same shared DB from another realm,
  // where the engine's `isScanning` guard can't reach it, so park the worker for
  // the duration — otherwise the wipe and the worker's appends interleave and
  // produce exactly the inconsistent tree we're repairing.
  suspendScanWorkerForChain(chainId)
  try {
    const { rescanFullUTXOMerkletreesAndWallets } = await loadRailgunSdk()
    // eslint-disable-next-line no-console
    console.warn(
      '[Railgun] proof failed against a stale merkletree — running full UTXO rescan',
      { chainId }
    )
    await rescanFullUTXOMerkletreesAndWallets(chain, walletId ? [walletId] : undefined)
    // eslint-disable-next-line no-console
    console.warn('[Railgun] full UTXO rescan complete — retry the transaction', { chainId })
  } catch (rescanErr) {
    // eslint-disable-next-line no-console
    console.warn('[Railgun] full UTXO rescan failed', chainId, rescanErr)
  } finally {
    resumeScanWorkerForChain(chainId)
    inFlightUTXORescans.delete(chainId)
  }
}

function getRequiredNetworkNameForChainId(chainId: number): NetworkName {
  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun POI: ${chainId}`)
  }
  return networkName
}

export async function getTXOsReceivedPOIStatusInfoForWalletForChain(params: {
  chainId: number
  walletId: string
}) {
  const { chainId, walletId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] getTXOsReceivedPOIStatusInfoForWallet', {
      chainId,
      walletId: walletId.slice(0, 8) + '…'
    })
  }
  const { getTXOsReceivedPOIStatusInfoForWallet } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return getTXOsReceivedPOIStatusInfoForWallet(TXIDVersion.V2_PoseidonMerkle, networkName, walletId)
}

export async function getTXOsSpentPOIStatusInfoForWalletForChain(params: {
  chainId: number
  walletId: string
}) {
  const { chainId, walletId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] getTXOsSpentPOIStatusInfoForWallet', {
      chainId,
      walletId: walletId.slice(0, 8) + '…'
    })
  }
  const { getTXOsSpentPOIStatusInfoForWallet } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return getTXOsSpentPOIStatusInfoForWallet(TXIDVersion.V2_PoseidonMerkle, networkName, walletId)
}

export async function generatePOIsForWalletAndRailgunTxidForChain(params: {
  chainId: number
  walletId: string
  railgunTxid: string
}) {
  const { chainId, walletId, railgunTxid } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] generatePOIsForWalletAndRailgunTxid', {
      chainId,
      walletId: walletId.slice(0, 8) + '…',
      railgunTxid: railgunTxid.slice(0, 16) + '…'
    })
  }
  const { generatePOIsForWalletAndRailgunTxid } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return generatePOIsForWalletAndRailgunTxid(
    TXIDVersion.V2_PoseidonMerkle,
    networkName,
    walletId,
    railgunTxid
  )
}

export async function generatePOIsForWalletForChain(params: { chainId: number; walletId: string }) {
  const { chainId, walletId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] generatePOIsForWallet', {
      chainId,
      walletId: walletId.slice(0, 8) + '…'
    })
  }
  const { generatePOIsForWallet } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return generatePOIsForWallet(networkName, walletId)
}

export async function refreshReceivePOIsForWalletForChain(params: {
  chainId: number
  walletId: string
}) {
  const { chainId, walletId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] refreshReceivePOIsForWallet', {
      chainId,
      walletId: walletId.slice(0, 8) + '…'
    })
  }
  const { refreshReceivePOIsForWallet } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return refreshReceivePOIsForWallet(TXIDVersion.V2_PoseidonMerkle, networkName, walletId)
}

export async function refreshSpentPOIsForWalletForChain(params: {
  chainId: number
  walletId: string
  railgunTxid?: string
}) {
  const { chainId, walletId, railgunTxid } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] refreshSpentPOIsForWallet', {
      chainId,
      walletId: walletId.slice(0, 8) + '…',
      ...(railgunTxid != null && { railgunTxid: railgunTxid.slice(0, 16) + '…' })
    })
  }
  const { refreshSpentPOIsForWallet } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return refreshSpentPOIsForWallet(
    TXIDVersion.V2_PoseidonMerkle,
    networkName,
    walletId,
    railgunTxid
  )
}

export async function getChainTxidsStillPendingSpentPOIsForChain(params: {
  chainId: number
  walletId: string
}) {
  const { chainId, walletId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] getChainTxidsStillPendingSpentPOIs', {
      chainId,
      walletId: walletId.slice(0, 8) + '…'
    })
  }
  const { getChainTxidsStillPendingSpentPOIs } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return getChainTxidsStillPendingSpentPOIs(TXIDVersion.V2_PoseidonMerkle, networkName, walletId)
}

export async function getSpendableReceivedChainTxidsForChain(params: {
  chainId: number
  walletId: string
}) {
  const { chainId, walletId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] getSpendableReceivedChainTxids', {
      chainId,
      walletId: walletId.slice(0, 8) + '…'
    })
  }
  const { getSpendableReceivedChainTxids } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return getSpendableReceivedChainTxids(TXIDVersion.V2_PoseidonMerkle, networkName, walletId)
}

export async function getPOIRequiredForNetworkForChain(params: { chainId: number }) {
  const { chainId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] getPOIRequiredForNetworkForChain', {
      chainId
    })
  }
  const networkName = getRequiredNetworkNameForChainId(chainId)
  // Engine providers live in the offscreen document under the offscreen flag;
  // calling POIRequired here in-process throws "Provider not yet loaded".
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen<boolean>({
      method: 'POIRequired_isRequiredForNetwork',
      args: [networkName]
    })
  }
  const { POIRequired } = await loadRailgunSdk()
  return POIRequired.isRequiredForNetwork(networkName)
}

export async function syncRailgunTransactionsV2ForChain(params: { chainId: number }) {
  const { chainId } = params
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun POI] syncRailgunTransactionsV2ForChain', {
      chainId
    })
  }
  const { syncRailgunTransactionsV2 } = await loadRailgunSdk()
  const networkName = getRequiredNetworkNameForChainId(chainId)
  return syncRailgunTransactionsV2(networkName)
}

export async function getShieldSignature(wallet: Wallet | HDNodeWallet): Promise<string> {
  const { getShieldPrivateKeySignatureMessage } = await loadRailgunSdk()
  const shieldSignatureMessage = getShieldPrivateKeySignatureMessage()
  const signature = await wallet.signMessage(shieldSignatureMessage)
  const shieldPrivateKey = keccak256(signature)
  return shieldPrivateKey
}

export type ERC20AllowanceAndApprove = {
  allowance: (owner: string, spender: string) => Promise<bigint>
  approve: (
    spender: string,
    amount: bigint
  ) => Promise<{
    wait: () => Promise<unknown>
  }>
}

// Read-only allowance lookup. Never broadcasts — safe to call during gas
// estimation. A token that returns `0x` (non-standard, wrong chain, or missing
// contract) is treated as zero allowance rather than throwing.
export async function readERC20Allowance(params: {
  tokenContract: Pick<ERC20AllowanceAndApprove, 'allowance'>
  owner: string
  spender: string
}): Promise<bigint> {
  const { tokenContract, owner, spender } = params
  try {
    return await tokenContract.allowance(owner, spender)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    if (msg.includes('BAD_DATA') || msg.includes('could not decode result')) {
      return 0n
    }
    throw err
  }
}

export async function ensureERC20Allowance(params: {
  tokenContract: ERC20AllowanceAndApprove
  owner: string
  spender: string
  amount: bigint
}): Promise<void> {
  const { tokenContract, owner, spender, amount } = params

  const allowance = await readERC20Allowance({ tokenContract, owner, spender })

  if (allowance < amount) {
    const approveTx = await tokenContract.approve(spender, amount)
    await approveTx.wait()
  }
}

export type RailgunGasSpeedName = 'slow' | 'medium' | 'fast' | 'ape'

export type RailgunGasFeeOption = {
  speed: RailgunGasSpeedName
  gasPriceWei: bigint
  maxFeePerGasWei: bigint
  maxPriorityFeePerGasWei: bigint
  gasFeeAmountNative: string
  gasFeeUsd?: string
}

export const RAILGUN_SPEED_LABELS: Record<RailgunGasSpeedName, string> = {
  slow: 'Slower',
  medium: 'Standard',
  fast: 'Faster',
  ape: 'Aggressive'
}

export const RAILGUN_SPEED_TIME_LABELS: Record<RailgunGasSpeedName, string> = {
  slow: 'Slow',
  medium: 'Normal',
  fast: 'Fast',
  ape: 'Fast'
}

type GasSpeed = 'slow' | 'standard' | 'fast' | 'aggressive'

const SPEED_MULTIPLIERS: Record<GasSpeed, number> = {
  slow: 0.9,
  standard: 1.0,
  fast: 1.3,
  aggressive: 1.8
}

const PRIORITY_MULTIPLIERS: Record<GasSpeed, number> = {
  slow: 0.8,
  standard: 1.0,
  fast: 1.5,
  aggressive: 2.5
}

const GWEI = 1_000_000_000n
const MIN_GAS_PRICE = 1_000_000n

const GAS_SPEEDS: GasSpeed[] = ['slow', 'standard', 'fast', 'aggressive']

function applyMultiplier(value: bigint, multiplier: number): bigint {
  const scaled = BigInt(Math.round(multiplier * 1000))
  const result = (value * scaled) / 1000n
  return result < MIN_GAS_PRICE ? MIN_GAS_PRICE : result
}

function railgunSpeedToGasSpeed(speed: RailgunGasSpeedName): GasSpeed {
  switch (speed) {
    case 'slow':
      return 'slow'
    case 'medium':
      return 'standard'
    case 'fast':
      return 'fast'
    case 'ape':
    default:
      return 'aggressive'
  }
}

function gasSpeedToRailgunSpeed(speed: GasSpeed): RailgunGasSpeedName {
  switch (speed) {
    case 'slow':
      return 'slow'
    case 'standard':
      return 'medium'
    case 'fast':
      return 'fast'
    case 'aggressive':
    default:
      return 'ape'
  }
}

export async function computeGasFeeOptionsNativeAndUsd(params: {
  gasEstimate: bigint
  chainId: number
  wallet: Wallet | HDNodeWallet
}): Promise<RailgunGasFeeOption[]> {
  const { gasEstimate, chainId, wallet } = params

  const provider = wallet.provider as JsonRpcProvider | null
  if (!provider) return []

  const feeData = await provider.getFeeData()
  const price = await fetchNativeAssetUsdPrice(chainId)
  const result: RailgunGasFeeOption[] = []

  for (const speed of GAS_SPEEDS) {
    let maxFeePerGas: bigint
    let maxPriorityFeePerGas: bigint
    let gasPrice: bigint

    if (feeData.maxFeePerGas != null) {
      const baseMaxFee = feeData.maxFeePerGas
      const basePriority = feeData.maxPriorityFeePerGas ?? GWEI

      maxFeePerGas = applyMultiplier(baseMaxFee, SPEED_MULTIPLIERS[speed])
      maxPriorityFeePerGas = applyMultiplier(basePriority, PRIORITY_MULTIPLIERS[speed])
      if (maxPriorityFeePerGas > maxFeePerGas) {
        maxPriorityFeePerGas = maxFeePerGas
      }

      gasPrice = maxFeePerGas
    } else {
      const basePrice = feeData.gasPrice ?? 5_000_000_000n
      gasPrice = applyMultiplier(basePrice, SPEED_MULTIPLIERS[speed])
      maxFeePerGas = gasPrice
      maxPriorityFeePerGas = 0n
    }

    const gasWei = gasEstimate * gasPrice
    const gasNative = Number(gasWei) / 1e18
    const gasFeeAmountNative = gasNative.toString()

    let gasFeeUsd: string | undefined
    if (price && price > 0) {
      gasFeeUsd = (gasNative * price).toFixed(2)
    }

    const railgunSpeed = gasSpeedToRailgunSpeed(speed)

    result.push({
      speed: railgunSpeed,
      gasPriceWei: gasPrice,
      maxFeePerGasWei: maxFeePerGas,
      maxPriorityFeePerGasWei: maxPriorityFeePerGas,
      gasFeeAmountNative,
      gasFeeUsd
    })
  }

  return result
}

export async function getGasDetailsForTransaction(
  network: NetworkName,
  gasEstimate: bigint,
  sendWithPublicWallet: boolean,
  wallet: Wallet | HDNodeWallet,
  chainId: number,
  preferredSpeed?: RailgunGasSpeedName
): Promise<TransactionGasDetails> {
  const evmGasType: EVMGasType = getEVMGasTypeForTransaction(network, sendWithPublicWallet)

  const provider = wallet.provider as JsonRpcProvider | null
  if (!provider) {
    switch (evmGasType) {
      case EVMGasType.Type0:
      case EVMGasType.Type1:
        return {
          evmGasType,
          gasEstimate,
          gasPrice: 0n
        }
      case EVMGasType.Type2:
      default:
        return {
          evmGasType,
          gasEstimate,
          maxFeePerGas: 0n,
          maxPriorityFeePerGas: 0n
        }
    }
  }

  const feeData = await provider.getFeeData()

  const gasSpeed: GasSpeed = preferredSpeed ? railgunSpeedToGasSpeed(preferredSpeed) : 'fast'

  if (evmGasType === EVMGasType.Type2 && feeData.maxFeePerGas != null) {
    const baseMaxFee = feeData.maxFeePerGas
    const basePriority = feeData.maxPriorityFeePerGas ?? GWEI

    let maxFeePerGas = applyMultiplier(baseMaxFee, SPEED_MULTIPLIERS[gasSpeed])
    let maxPriorityFeePerGas = applyMultiplier(basePriority, PRIORITY_MULTIPLIERS[gasSpeed])
    if (maxPriorityFeePerGas > maxFeePerGas) {
      maxPriorityFeePerGas = maxFeePerGas
    }

    return {
      evmGasType,
      gasEstimate,
      maxFeePerGas,
      maxPriorityFeePerGas
    }
  }

  const basePrice = feeData.gasPrice ?? 5_000_000_000n
  const gasPrice = applyMultiplier(basePrice, SPEED_MULTIPLIERS[gasSpeed])

  switch (evmGasType) {
    case EVMGasType.Type0:
    case EVMGasType.Type1:
      return {
        evmGasType,
        gasEstimate,
        gasPrice
      }
    case EVMGasType.Type2:
    default:
      return {
        evmGasType,
        gasEstimate,
        maxFeePerGas: gasPrice,
        maxPriorityFeePerGas: gasPrice / 10n
      }
  }
}

export async function computeGasFeeNativeAndUsd(params: {
  gasEstimate: bigint
  gasDetails: TransactionGasDetails
  chainId: number
}): Promise<{ gasFeeAmountNative: string; gasFeeUsd?: string }> {
  const { gasEstimate, gasDetails, chainId } = params

  let gasPriceWei: bigint = 0n
  if ('gasPrice' in gasDetails && gasDetails.gasPrice != null) {
    gasPriceWei = gasDetails.gasPrice
  } else if ('maxFeePerGas' in gasDetails && gasDetails.maxFeePerGas != null) {
    gasPriceWei = gasDetails.maxFeePerGas
  }

  const gasWei = gasEstimate * gasPriceWei
  const gasFeeAmountNative = (Number(gasWei) / 1e18).toString()
  let gasFeeUsd: string | undefined

  const price = await fetchNativeAssetUsdPrice(chainId)
  if (price && price > 0) {
    const gasNative = Number(gasWei) / 1e18
    gasFeeUsd = (gasNative * price).toFixed(2)
  }

  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[Railgun gas]', {
      chainId,
      gasEstimate: gasEstimate.toString(),
      gasFeeAmountNative,
      gasFeeUsd
    })
  }

  return { gasFeeAmountNative, gasFeeUsd }
}

export type RailgunTxType = 'shield' | 'unshield' | 'transfer'

/**
 * Maps raw Railgun errors to short, user-friendly messages.
 * Used for failures when shield/unshield/private transfer proof/execute fails (not gas estimation).
 */
export function getErrorMessage(type: RailgunTxType, err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  const full = msg.toLowerCase()

  const action =
    type === 'shield' ? 'shielding' : type === 'unshield' ? 'unshielding' : 'private transfer'
  const actionCapital =
    type === 'shield' ? 'Shielding' : type === 'unshield' ? 'Unshielding' : 'Private transfer'

  // An input note already nullified on-chain but still listed locally as spendable
  // (spent-state lag / shared-note passphrase-collision). A light balance refresh
  // re-scans the nullifier and drops it, so guide the user to retry rather than
  // showing the generic revert message below (which would otherwise catch it).
  if (isNoteAlreadySpentError(err)) {
    return 'Your private balance is syncing after a recent transaction. Please try again in a few seconds.'
  }

  // RAILGUN circuit artifacts for variants with > 5 input notes are not
  // bundled in the extension (Chrome MV3 forbids remote-code fetches of the
  // larger `.wasm` files). When the user's private balance is fragmented
  // across many small notes, the SDK requests an unbundled variant and our
  // remote-code guard rejects the attempted IPFS download. Surface this as a
  // consolidate-first hint instead of a generic failure.
  if (
    full.includes('remote railgun artifact fetch blocked') ||
    full.includes('could not retrieve zkey artifact') ||
    full.includes('could not retrieve wasm artifact') ||
    full.includes('could not retrieve vkey artifact') ||
    full.includes('bundled artifact missing') ||
    /no artifacts for inputs:\s*([6-9]|1[0-3])-/.test(full)
  ) {
    return `Your private balance is split across too many small notes for this ${action}. Try sending a smaller amount, or break the transaction into multiple smaller ones.`
  }

  // Self-signed railgun txs (shield/unshield/private-transfer) are broadcast by
  // the user's PUBLIC wallet, which pays the on-chain gas in the source chain's
  // NATIVE token. When that wallet lacks native gas the broadcast reverts with an
  // "insufficient funds for gas / intrinsic transaction cost" error. That is NOT
  // a shielded-amount problem — telling the user to "try a smaller amount" is
  // wrong and wastes their time, so map it to a gas-specific hint first.
  if (
    full.includes('insufficient funds for gas') ||
    full.includes('insufficient funds for intrinsic') ||
    full.includes('insufficient funds for transfer') ||
    (full.includes('insufficient funds') &&
      (full.includes('gas') || full.includes('intrinsic') || full.includes('fee')))
  ) {
    return `Not enough native gas to broadcast this ${action}. Add the source network's gas token to your wallet and try again.`
  }

  if (
    full.includes('insufficient') ||
    full.includes('exceeds balance') ||
    full.includes('transfer amount exceeds') ||
    full.includes('balance too low') ||
    full.includes('amount exceeds') ||
    full.includes('exceeds allowance')
  ) {
    return `Insufficient amount for ${action}. Check your balance and try a smaller amount.`
  }

  if (
    full.includes('allowance') ||
    (full.includes('approve') && !full.includes('approval completed'))
  ) {
    return `Token approval for ${action} failed. You may need to approve this token first.`
  }

  if (
    full.includes('missing revert data') ||
    full.includes('revert data') ||
    full.includes('call_exception') ||
    full.includes('execution reverted') ||
    full.includes('transaction would revert') ||
    full.includes('simulation failed') ||
    full.includes('rpc connection error')
  ) {
    return `Transaction would fail for ${action}. Token may not be supported on this network, or check balance and approval.`
  }

  if (
    full.includes('bad_data') ||
    full.includes('could not decode') ||
    full.includes('token not supported') ||
    full.includes('unsupported token')
  ) {
    return `This token is not supported for ${action} on this network.`
  }

  if (
    full.includes('unsupported chainid') ||
    full.includes('no rpc url') ||
    full.includes('no proxy contract') ||
    full.includes('no wrapped base token') ||
    full.includes('rpc connection') ||
    full.includes('network error') ||
    full.includes('timeout') ||
    full.includes('econnrefused')
  ) {
    return `Network error during ${actionCapital.toLowerCase()}. Check your connection or try again later.`
  }

  // Circuit proof failed at witness generation (MerkleProofVerifier) — the local
  // UTXO merkletree is out of sync with the note being spent. A plain "try again"
  // can never succeed until the tree is rebuilt, so say what's actually happening.
  // The caller fires a full UTXO rescan in the background (see
  // triggerFullUTXORescanForChain); the user just needs to wait and retry.
  if (isProvingMerkleFailure(err)) {
    return `Your private balance is out of sync, so this ${action} couldn't be proven. We're re-syncing your privacy data — please wait a moment and try again.`
  }

  // The engine has no wallet under the id the page addressed (SDK `walletForID`),
  // or this worker's wallet map is empty — i.e. the railgun worker was respawned
  // (lock / crash) and the wallet was never re-loaded into it. The worker now
  // self-heals this before every spend (see `ensureEngineWalletLoaded`), so
  // reaching here means the re-load itself failed. "Use another token" is wrong
  // and misleading — the token is fine, the private wallet just isn't available.
  if (
    full.includes('no railgun wallet for id') ||
    full.includes('wallet not loaded') ||
    full.includes('railgun wallet id mismatch') ||
    full.includes('railgun wallet metadata missing')
  ) {
    return `Your private wallet isn't loaded yet, so this ${action} couldn't start. Wait for privacy data to finish loading (or unlock the app again) and retry.`
  }

  return `${actionCapital} failed. Please try again or use another token.`
}
